/*
 * «Min moské-oppsett»: hvilken topp og hvilken rekkefølge seksjonene har på Min moské i appene.
 * Seksjonene kan bare flyttes. Det eneste som kan skjules er kontaktinfoen og fasilitetsbrikkene.
 * Lagres på moskeen som `myMosqueLayout` (null = standard).
 */

export type HeroStyle = "FULL" | "COMPACT";

export const SECTION_KEYS = [
  "FRIDAY_JUMMAH",
  "PRAYER_TIMES",
  "JUMMAH",
  "EID",
  "INFORMATION",
  "RAMADAN",
  "TARAWEEH",
  "CAMPAIGNS",
  "QUICK_ACTIONS",
  "POSTERS",
  "EVENTS",
  "ARTICLES",
  "CONTACT",
] as const;

export type SectionKey = (typeof SECTION_KEYS)[number];

/** Delene av Min moské som moské-admin kan skjule (i seksjonen «Kontakt, adresse og om moskeen»). */
export const HIDEABLE_PARTS = ["CONTACT_INFO", "FACILITIES"] as const;

export type HideablePart = (typeof HIDEABLE_PARTS)[number];

export const HIDEABLE_INFO: Record<HideablePart, { name: string; text: string }> =
  {
    CONTACT_INFO: {
      name: "Kontaktinfo",
      text: "Adresse, veibeskrivelse, kontaktperson, telefon, e-post og nettside",
    },
    FACILITIES: {
      name: "Fasiliteter",
      text: "Brikkene med fasiliteter i «Om moskeen» (parkering, wudu, kjøkken …)",
    },
  };

export interface MyMosqueLayout {
  hero: HeroStyle;
  sections: SectionKey[];
  hidden: HideablePart[];
}

/** Bønnetider i dag vises alltid øverst i sin plass. */
export const ALWAYS_VISIBLE: SectionKey = "PRAYER_TIMES";

/** Seksjonene som bare vises i sin sesong (fargelagt i admin-lista). */
export const SEASONAL: readonly SectionKey[] = ["EID", "RAMADAN", "TARAWEEH"];

/** Jummah-tidene henger fast rett under Bønnetider i dag og flyttes sammen med dem. */
export const LINKED_TO_PRAYER_TIMES: SectionKey = "JUMMAH";

export const SECTION_INFO: Record<SectionKey, { name: string; when: string }> =
  {
    FRIDAY_JUMMAH: {
      name: "Fredag: Finn nærmeste jummah",
      when: "Fredag fra soloppgang til Asr",
    },
    PRAYER_TIMES: {
      name: "Bønnetider i dag og jummah",
      when: "Alltid. Jummah-tidene står rett under og flyttes sammen med bønnetidene.",
    },
    EID: { name: "Eid", when: "I Eid-sesongen" },
    JUMMAH: { name: "Jummah-tider", when: "Når moskeen har jummah" },
    INFORMATION: {
      name: "Informasjon fra moskeen",
      when: "Når moskeen har informasjon",
    },
    RAMADAN: { name: "Ramadan", when: "Når det finnes Ramadan-data" },
    TARAWEEH: { name: "Taraweeh", when: "Når det finnes taraweeh" },
    CAMPAIGNS: {
      name: "Innsamlinger",
      when: "Når det finnes aktive kampanjer",
    },
    QUICK_ACTIONS: { name: "Snarveier (fasiliteter og alarm)", when: "Alltid" },
    POSTERS: { name: "Plakater", when: "Når det finnes plakater" },
    EVENTS: { name: "Aktiviteter", when: "Når det finnes aktiviteter" },
    ARTICLES: { name: "Artikler", when: "Når det finnes artikler" },
    CONTACT: {
      name: "Kontakt, adresse og om moskeen",
      when: "Når moskeen har kontaktinfo eller informasjon",
    },
  };

export const defaultLayout = (): MyMosqueLayout => ({
  hero: "FULL",
  sections: [...SECTION_KEYS],
  hidden: [],
});

const isHideablePart = (value: unknown): value is HideablePart =>
  typeof value === "string" &&
  (HIDEABLE_PARTS as readonly string[]).includes(value);

const isSectionKey = (value: unknown): value is SectionKey =>
  typeof value === "string" &&
  (SECTION_KEYS as readonly string[]).includes(value);

/**
 * Samme regel som i appene: `sections` i rekkefølge uten ukjente nøkler og dubletter, manglende
 * nøkler sist i standardrekkefølge, og Jummah rett under Bønnetider i dag. `hidden` bare
 * kontaktinfo og fasiliteter (gamle skjulte seksjoner droppes).
 * Ukjent eller manglende `hero` blir FULL.
 */
export const normalizeLayout = (raw: unknown): MyMosqueLayout => {
  const value =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const sections: SectionKey[] = [];
  (Array.isArray(value.sections) ? value.sections : []).forEach((key) => {
    if (isSectionKey(key) && !sections.includes(key)) sections.push(key);
  });
  SECTION_KEYS.forEach((key) => {
    if (!sections.includes(key)) sections.push(key);
  });
  sections.splice(sections.indexOf(LINKED_TO_PRAYER_TIMES), 1);
  sections.splice(
    sections.indexOf(ALWAYS_VISIBLE) + 1,
    0,
    LINKED_TO_PRAYER_TIMES,
  );
  const hiddenRaw = Array.isArray(value.hidden) ? value.hidden : [];
  const hidden = HIDEABLE_PARTS.filter(
    (part) => isHideablePart(part) && hiddenRaw.includes(part),
  );
  return {
    hero: value.hero === "COMPACT" ? "COMPACT" : "FULL",
    sections,
    hidden,
  };
};

/** Radene admin flytter på: Jummah er en del av raden til Bønnetider i dag. */
export const movableRows = (layout: MyMosqueLayout): SectionKey[] =>
  layout.sections.filter((key) => key !== LINKED_TO_PRAYER_TIMES);

/** Ny rekkefølge fra radene; Jummah settes inn igjen rett under Bønnetider i dag. */
export const withRows = (
  layout: MyMosqueLayout,
  rows: SectionKey[],
): MyMosqueLayout => normalizeLayout({ ...layout, sections: rows });

/** Flytter ett element fra `from` til `to` (begge indekser i listen). */
export const moveItem = <T>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(Math.min(to, next.length), 0, item);
  return next;
};

export const sameLayout = (a: MyMosqueLayout, b: MyMosqueLayout) =>
  a.hero === b.hero &&
  a.sections.join() === b.sections.join() &&
  [...a.hidden].sort().join() === [...b.hidden].sort().join();
